import React from 'react';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Separator } from '../ui/separator';
import { BarChart, Tag, User, Star, TrendingUp } from 'lucide-react';
import { getSearchFacetClassName } from '../../utils/tagColors';

interface RangeInfo {
  min: number;
  max: number;
  average: number;
}

interface SearchFacets {
  categories: Record<string, number>;
  tags: Record<string, number>;
  submitters: Record<string, number>;
  usesRange: RangeInfo;
  likesRange: RangeInfo;
  ratingRange: RangeInfo;
  contentLengthRange: RangeInfo;
}

interface SearchFacetsProps {
  facets: SearchFacets;
  onCategoryClick?: (category: string) => void;
  onTagClick?: (tag: string) => void;
  onSubmitterClick?: (submitter: string) => void;
  className?: string;
}

export const SearchFacetsPanel: React.FC<SearchFacetsProps> = ({
  facets,
  onCategoryClick,
  onTagClick,
  onSubmitterClick,
  className
}) => {
  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatRange = (range: RangeInfo, unit: string = '') => {
    if (range.min === range.max) {
      return `${formatNumber(range.min)}${unit}`;
    }
    return `${formatNumber(range.min)}${unit} - ${formatNumber(range.max)}${unit}`;
  };

  return (
    <div className={className}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart className="w-4 h-4" />
            Search Insights
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Categories */}
          {Object.keys(facets.categories).length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <Tag className="w-4 h-4 text-blue-500" />
                Categories
              </h4>
              <div className="space-y-2">
                {Object.entries(facets.categories)
                  .slice(0, 8)
                  .map(([category, count]) => (
                    <div
                      key={category}
                      className="flex items-center justify-between"
                    >
                      <button
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline text-left flex-1 truncate"
                        onClick={() => onCategoryClick?.(category)}
                        title={category}
                      >
                        {category}
                      </button>
                      <Badge variant="secondary" className="text-xs ml-2">
                        {count}
                      </Badge>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {Object.keys(facets.tags).length > 0 && (
            <div>
              <Separator />
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2 mt-4">
                <Tag className="w-4 h-4 text-green-500" />
                Popular Tags
              </h4>
              <div className="flex flex-wrap gap-2">
                {Object.entries(facets.tags)
                  .slice(0, 12)
                  .map(([tag, count]) => {
                    return (
                      <button
                        key={tag}
                        className={getSearchFacetClassName(tag)}
                        onClick={() => onTagClick?.(tag)}
                      >
                        <span>{tag}</span>
                        <span className="font-medium opacity-75">({count})</span>
                      </button>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Top Submitters */}
          {Object.keys(facets.submitters).length > 0 && (
            <div>
              <Separator />
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2 mt-4">
                <User className="w-4 h-4 text-purple-500" />
                Top Contributors
              </h4>
              <div className="space-y-2">
                {Object.entries(facets.submitters)
                  .slice(0, 5)
                  .map(([submitter, count]) => (
                    <div
                      key={submitter}
                      className="flex items-center justify-between"
                    >
                      <button
                        className="text-sm text-purple-600 hover:text-purple-800 hover:underline text-left flex-1 truncate"
                        onClick={() => onSubmitterClick?.(submitter)}
                        title={submitter}
                      >
                        {submitter}
                      </button>
                      <Badge variant="outline" className="text-xs ml-2 border-purple-200 text-purple-700">
                        {count}
                      </Badge>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Metrics Overview */}
          <div>
            <Separator />
            <h4 className="text-sm font-medium mb-3 flex items-center gap-2 mt-4">
              <TrendingUp className="w-4 h-4 text-orange-500" />
              Metrics Overview
            </h4>
            <div className="grid grid-cols-2 gap-3 text-xs">
              {/* Uses */}
              <div className="p-2 bg-orange-50 rounded-md">
                <div className="text-orange-700 font-medium">Uses</div>
                <div className="text-orange-600">
                  {formatRange(facets.usesRange)}
                </div>
                <div className="text-orange-500 text-xs">
                  Avg: {formatNumber(facets.usesRange.average)}
                </div>
              </div>

              {/* Likes */}
              <div className="p-2 bg-red-50 rounded-md">
                <div className="text-red-700 font-medium">Likes</div>
                <div className="text-red-600">
                  {formatRange(facets.likesRange)}
                </div>
                <div className="text-red-500 text-xs">
                  Avg: {formatNumber(facets.likesRange.average)}
                </div>
              </div>

              {/* Rating */}
              {facets.ratingRange.max > 0 && (
                <div className="p-2 bg-yellow-50 rounded-md">
                  <div className="text-yellow-700 font-medium flex items-center gap-1">
                    <Star className="w-3 h-3" />
                    Rating
                  </div>
                  <div className="text-yellow-600">
                    {facets.ratingRange.min.toFixed(1)} - {facets.ratingRange.max.toFixed(1)}
                  </div>
                  <div className="text-yellow-500 text-xs">
                    Avg: {facets.ratingRange.average.toFixed(1)}
                  </div>
                </div>
              )}

              {/* Content Length */}
              <div className="p-2 bg-indigo-50 rounded-md">
                <div className="text-indigo-700 font-medium">Content</div>
                <div className="text-indigo-600">
                  {formatRange(facets.contentLengthRange)} chars
                </div>
                <div className="text-indigo-500 text-xs">
                  Avg: {formatNumber(facets.contentLengthRange.average)} chars
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};